/**
 * Générateur — Résoudre un problème relevant de la division euclidienne
 * Attendu ID : 619 | Niveau : 6ème | Domaine : NC (Nombres et calculs)
 *
 * Ce générateur contient 9 EXERCICES DIDACTIQUEMENT DISTINCTS (chacun avec
 * son propre generatorKey), certains déclinés en plusieurs formats UI :
 *
 *   1. `partage_objets`             → QCM unique + Saisie
 *      Sens partition : a objets répartis équitablement entre b personnes.
 *      Objet cherché : (q, r) = (part, reste).
 *
 *   2. `groupement_paquets`         → QCM unique + Saisie
 *      Sens quotition : a objets groupés en paquets de b.
 *      Objet cherché : (q, r) = (nombre de paquets, reste).
 *
 *   3. `complement_groupe_plus`     → QCM unique + Saisie
 *      « Combien manque-t-il pour un groupe de plus ? » → b − r (si r > 0).
 *
 *   4. `transport_min`              → QCM unique + Saisie
 *      Arrondi au supérieur : ceil(a/b) = q (si r=0) ou q+1 (si r>0).
 *
 *   5. `transport_dernier`          → QCM unique + Saisie
 *      Remplissage du dernier contenant : r (si r>0) — contrainte r>0.
 *
 *   6. `invites_recalcul`           → Saisie uniquement
 *      Recalcul d'un partage après ajout d'invités (a, b₀ + k).
 *
 *   7. `choisir_calculs_eucl`       → QCM multiple uniquement
 *      Discrimination : choisir les écritures valides d'un même calcul.
 *
 *   8. `relier_enonces_eucl`        → Relier uniquement
 *      4 énoncés courts à apparier à 4 opérations a ÷ b.
 *
 *   9. `vrai_faux_interpretation`   → V/F uniquement
 *      1 problème commun + 6 interprétations (3V / 3F).
 *
 * Règles dures de la fiche cluster 10 (attendu 619) :
 *   - Tous les triplets (a, b, q, r) sont vérifiés par isValidEucl avant émission.
 *   - Le quotient q ≥ 1 partout (sinon l'énoncé perd son sens).
 *   - Pour `complement_groupe_plus` et `transport_dernier` : r ≥ 1 obligatoire.
 *   - Réponses toujours entières : les saisies refusent virgule/point.
 *
 * Les 4 grandes familles d'erreur contextualisées (distracteurs) :
 *   1. Oubli du reste / oubli du +1 de transport
 *   2. Confusion quotient/reste (rôles échangés)
 *   3. Complément faux (r au lieu de b−r)
 *   4. Division décimale sur grandeur discrète
 *   5. Ordre inversé (b ÷ a)
 */

import {
    randomChoice, randomChoiceMultiple, randInt, shuffle,
    makeQcm, makeSaisie, makeVraiFaux, makeRelier, makeTexteTrou,
} from '../../../utils';

// ═══════════════════════════════════════════════════════════
// CONSTANTES & LABELS
// ═══════════════════════════════════════════════════════════

const ATTENDU_ID = 619;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_PARTAGE_QCM       = 'Partage équitable d\'objets discrets (QCM)';
const L_PARTAGE_SAI       = 'Partage équitable d\'objets discrets (Saisie)';
const L_GROUPEMENT_QCM    = 'Groupement en paquets complets + reste (QCM)';
const L_GROUPEMENT_SAI    = 'Groupement en paquets complets + reste (Saisie)';
const L_COMPLEMENT_QCM    = 'Complément au diviseur pour un groupe de plus (QCM)';
const L_COMPLEMENT_SAI    = 'Complément au diviseur pour un groupe de plus (Saisie)';
const L_TRANSPORT_MIN_QCM = 'Nombre minimum de contenants pour un transport (QCM)';
const L_TRANSPORT_MIN_SAI = 'Nombre minimum de contenants pour un transport (Saisie)';
const L_TRANSPORT_DER_QCM = 'Contenu du dernier contenant (QCM)';
const L_TRANSPORT_DER_SAI = 'Contenu du dernier contenant (Saisie)';
const L_INVITES           = 'Recalcul d\'un partage après ajout d\'invités (Saisie)';
const L_CHOISIR           = 'Discrimination : choisir les écritures correctes d\'une division euclidienne';
const L_RELIER            = "Reconnaître l'opération à utiliser (+, −, ×, ÷) pour résoudre un problème";
const L_VF                = 'Vrai/Faux : 6 interprétations d\'un même problème';
// Libellé de la carte qui réunit le QCM multiple et le Vrai/Faux (revue
// 2026-08-31) : même tâche — juger ce qu'on peut dire d'une division faite.
const L_INTERPRETER       = 'Interpréter le résultat d\'une division euclidienne';
const L_PARTAGE_GROUPEMENT = 'Partage ou groupement — lire le quotient et le reste';

// Rappel affiché en tête des énoncés calculatoires (revue 2026-08-25, choix
// prof). Pas de rappel sur
// choisir_calculs (on juge des écritures) ni relier_enonces (on reconnaît
// l'opération) : l'élève n'y calcule pas.
// Libellé NEUTRE sur l'opération (revue 2026-08-25) : reconnaître que la
// situation appelle une division fait partie de la tâche — le rappel ne doit
// pas la vendre.
const RAPPEL_BROUILLON = '<p class="italic text-slate-400">✏️ Pense à poser tes calculs sur ton cahier ou au brouillon avant de répondre.</p>';

// ═══════════════════════════════════════════════════════════
// HELPERS ARITHMÉTIQUES
// ═══════════════════════════════════════════════════════════

const divEucl = (a, b) => {
    if (!Number.isInteger(a) || !Number.isInteger(b) || b <= 0) {
        throw new Error(`divEucl: invalide a=${a}, b=${b}`);
    }
    return { q: Math.floor(a / b), r: a % b };
};

const isValidEucl = (a, b, q, r) =>
    Number.isInteger(a) && Number.isInteger(b) && Number.isInteger(q) && Number.isInteger(r)
    && b > 0 && q >= 0 && r >= 0 && r < b && q * b + r === a;

const ceilDiv = (a, b) => Math.ceil(a / b);

const toDecApprox = (a, b) => (a / b).toFixed(2).replace('.', ',');

/** Nettoyage des distracteurs : unicité + complétion par fallback. */
const cleanDistracteurs = (bonne, dists, n, fallbackFn) => {
    const out = [];
    const seen = new Set([String(bonne)]);
    for (const d of dists) {
        const s = String(d);
        if (!seen.has(s)) { out.push(d); seen.add(s); }
    }
    let guard = 0;
    while (out.length < n && guard < 40) {
        const cand = fallbackFn(guard);
        const s = String(cand);
        if (!seen.has(s)) { out.push(cand); seen.add(s); }
        guard++;
    }
    return out.slice(0, n);
};

// ═══════════════════════════════════════════════════════════
// POOLS DE TRIPLETS — tous vérifiés par isValidEucl
// ═══════════════════════════════════════════════════════════

/**
 * POOL_A — Partage équitable, sous-famille A.
 * Contraintes : b ∈ [2, 9], q ≥ 2, a ∈ [20, 500]. Mélange r=0 et r>0.
 */
const POOL_A = [
    [26, 3, 8, 2],
    [87, 4, 21, 3],
    [47, 6, 7, 5],
    [158, 6, 26, 2],
    [73, 5, 14, 3],
    [100, 7, 14, 2],
    [45, 8, 5, 5],
    [200, 9, 22, 2],
    [156, 6, 26, 0],
    [89, 7, 12, 5],
    [123, 5, 24, 3],
    [240, 8, 30, 0],
    [55, 4, 13, 3],
    [72, 9, 8, 0],
    [134, 3, 44, 2],
    [189, 6, 31, 3],
    [250, 7, 35, 5],
    [365, 9, 40, 5],
    [420, 8, 52, 4],
    [96, 5, 19, 1],
    [144, 6, 24, 0],
    [85, 4, 21, 1],
    [117, 9, 13, 0],
    [63, 5, 12, 3],
    [38, 7, 5, 3],
];

/**
 * POOL_B — Groupement, sous-famille B.
 * Contraintes : b ∈ [5, 50], q ≥ 2, a ∈ [50, 2000].
 */
const POOL_B = [
    [295, 18, 16, 7],
    [158, 7, 22, 4],
    [1000, 12, 83, 4],
    [390, 14, 27, 12],
    [450, 25, 18, 0],
    [536, 45, 11, 41],
    [686, 15, 45, 11],
    [958, 17, 56, 6],
    [800, 22, 36, 8],
    [1456, 26, 56, 0],
    [729, 17, 42, 15],
    [1234, 19, 64, 18],
    [612, 16, 38, 4],
    [987, 24, 41, 3],
    [1500, 27, 55, 15],
    [450, 12, 37, 6],
    [777, 25, 31, 2],
    [856, 37, 23, 5],
    [672, 30, 22, 12],
    [365, 20, 18, 5],
    [200, 12, 16, 8],
    [350, 9, 38, 8],   // b=9 (petit paquet) encore admissible
    [275, 8, 34, 3],
    [512, 11, 46, 6],
    [640, 14, 45, 10],
];

/**
 * POOL_D — Transport, sous-famille D.
 * Contraintes : a ∈ [20, 600], b ∈ [4, 60]. Mélange r=0 (1/3) et r>0 (2/3).
 */
const POOL_D = [
    [536, 45, 11, 41],
    [56, 8, 7, 0],
    [47, 6, 7, 5],
    [390, 14, 27, 12],
    [85, 12, 7, 1],
    [200, 25, 8, 0],
    [125, 15, 8, 5],
    [300, 30, 10, 0],
    [95, 20, 4, 15],
    [450, 50, 9, 0],
    [73, 8, 9, 1],
    [150, 18, 8, 6],
    [350, 40, 8, 30],
    [60, 4, 15, 0],
    [112, 12, 9, 4],
    [240, 15, 16, 0],
    [175, 22, 7, 21],
    [88, 10, 8, 8],
    [310, 35, 8, 30],
    [420, 50, 8, 20],
];

/**
 * POOL_F — Triplets (a, b₀, k) pour invites_recalcul.
 * Contraintes : b = b₀ + k ∈ [3, 20], a ∈ [20, 300], q ≥ 1 (sur b final), r ≥ 0.
 */
const POOL_F_RAW = [
    [27, 9, 4],   // 27/13 = 2 r 1
    [50, 7, 3],   // 50/10 = 5 r 0
    [65, 5, 3],   // 65/8 = 8 r 1
    [80, 9, 2],   // 80/11 = 7 r 3
    [100, 9, 3],  // 100/12 = 8 r 4
    [48, 5, 3],   // 48/8 = 6 r 0
    [75, 6, 4],   // 75/10 = 7 r 5
    [150, 8, 2],  // 150/10 = 15 r 0
    [60, 7, 3],   // 60/10 = 6 r 0
    [90, 6, 3],   // 90/9 = 10 r 0
    [120, 7, 4],  // 120/11 = 10 r 10
    [85, 5, 3],   // 85/8 = 10 r 5
    [55, 4, 3],   // 55/7 = 7 r 6
    [180, 9, 3],  // 180/12 = 15 r 0
    [72, 6, 3],   // 72/9 = 8 r 0
];

// Filtrage préventif : tous les triplets doivent respecter les contraintes.
const POOL_A_V = POOL_A.filter(([a, b, q, r]) => isValidEucl(a, b, q, r) && q >= 2);
const POOL_B_V = POOL_B.filter(([a, b, q, r]) => isValidEucl(a, b, q, r) && q >= 2);
const POOL_C_V = POOL_B_V.filter(([, , , r]) => r > 0);              // complement : r > 0
const POOL_D_V = POOL_D.filter(([a, b, q, r]) => isValidEucl(a, b, q, r) && q >= 1);
const POOL_E_V = POOL_D_V.filter(([, , , r]) => r >= 2);             // transport_dernier : r >= 2 (évite "1 passagers")
const POOL_F_V = POOL_F_RAW.filter(([a, b0, k]) => {
    const b = b0 + k;
    if (b < 3 || b > 20) return false;
    const { q, r } = divEucl(a, b);
    return q >= 1 && isValidEucl(a, b, q, r);
});

// ═══════════════════════════════════════════════════════════
// POOLS DE CONTEXTES & PRÉNOMS
// ═══════════════════════════════════════════════════════════

const PRENOMS = [
    { nom: 'Léa', fem: true },
    { nom: 'Zoé', fem: true },
    { nom: 'Maya', fem: true },
    { nom: 'Chloé', fem: true },
    { nom: 'Jade', fem: true },
    { nom: 'Emma', fem: true },
    { nom: 'Lila', fem: true },
    { nom: 'Noé', fem: false },
    { nom: 'Hugo', fem: false },
    { nom: 'Tom', fem: false },
    { nom: 'Yanis', fem: false },
    { nom: 'Adam', fem: false },
    { nom: 'Raphaël', fem: false },
];

const pickPrenom = () => randomChoice(PRENOMS);

/** Contextes partage : objets discrets partagés entre recipients. */
// genreObj : 'm' | 'f' — genre de l'objet (bille = f, bonbon = m)
const CTX_PARTAGE = [
    { objet: 'billes',        sing: 'bille',        genreObj: 'f', rec_pl: 'amis',       rec_sg: 'ami' },
    { objet: 'bonbons',       sing: 'bonbon',       genreObj: 'm', rec_pl: 'enfants',    rec_sg: 'enfant' },
    { objet: 'cartes',        sing: 'carte',        genreObj: 'f', rec_pl: 'amis',       rec_sg: 'ami' },
    { objet: 'images',        sing: 'image',        genreObj: 'f', rec_pl: 'enfants',    rec_sg: 'enfant' },
    { objet: 'autocollants',  sing: 'autocollant',  genreObj: 'm', rec_pl: 'camarades',  rec_sg: 'camarade' },
    { objet: 'crayons',       sing: 'crayon',       genreObj: 'm', rec_pl: 'élèves',     rec_sg: 'élève' },
    { objet: 'gommes',        sing: 'gomme',        genreObj: 'f', rec_pl: 'amis',       rec_sg: 'ami' },
    { objet: 'chocolats',     sing: 'chocolat',     genreObj: 'm', rec_pl: 'invités',    rec_sg: 'invité' },
    { objet: 'biscuits',      sing: 'biscuit',      genreObj: 'm', rec_pl: 'enfants',    rec_sg: 'enfant' },
    { objet: 'fondants',      sing: 'fondant',      genreObj: 'm', rec_pl: 'invités',    rec_sg: 'invité' },
];

/** Contextes groupement : objets regroupés en paquets. */
// genreObj : genre de l'objet ; genreGrp : genre du contenant.
const CTX_GROUPEMENT = [
    { objet: 'roses',    sing: 'rose',    genreObj: 'f', grp_pl: 'bouquets', grp_sg: 'bouquet', genreGrp: 'm' },
    { objet: 'tulipes',  sing: 'tulipe',  genreObj: 'f', grp_pl: 'bouquets', grp_sg: 'bouquet', genreGrp: 'm' },
    { objet: 'fleurs',   sing: 'fleur',   genreObj: 'f', grp_pl: 'bouquets', grp_sg: 'bouquet', genreGrp: 'm' },
    { objet: 'bonbons',  sing: 'bonbon',  genreObj: 'm', grp_pl: 'sachets',  grp_sg: 'sachet',  genreGrp: 'm' },
    { objet: 'gâteaux',  sing: 'gâteau',  genreObj: 'm', grp_pl: 'boîtes',   grp_sg: 'boîte',   genreGrp: 'f' },
    { objet: 'livres',   sing: 'livre',   genreObj: 'm', grp_pl: 'rayons',   grp_sg: 'rayon',   genreGrp: 'm' },
    { objet: 'photos',   sing: 'photo',   genreObj: 'f', grp_pl: 'pages',    grp_sg: 'page',    genreGrp: 'f' },
    { objet: 'biscuits', sing: 'biscuit', genreObj: 'm', grp_pl: 'paquets',  grp_sg: 'paquet',  genreGrp: 'm' },
    { objet: 'cartes',   sing: 'carte',   genreObj: 'f', grp_pl: 'boîtes',   grp_sg: 'boîte',   genreGrp: 'f' },
];

/** Contextes transport. `sujet` est le nom pluriel. */
// genreSujet : genre grammatical du sujet (passagers = m, personnes = f, élèves/colis = m mixte).
const CTX_TRANSPORT = [
    { sujet: 'passagers', sujet_sg: 'passager', genreSujet: 'm', vehic_pl: 'bus',          vehic_sg: 'bus',          genreVehic: 'm', place: 'places' },
    { sujet: 'élèves',    sujet_sg: 'élève',    genreSujet: 'm', vehic_pl: 'autocars',     vehic_sg: 'autocar',      genreVehic: 'm', place: 'places' },
    { sujet: 'colis',     sujet_sg: 'colis',    genreSujet: 'm', vehic_pl: 'camionnettes', vehic_sg: 'camionnette',  genreVehic: 'f', place: 'places' },
    { sujet: 'livres',    sujet_sg: 'livre',    genreSujet: 'm', vehic_pl: 'sacs',         vehic_sg: 'sac',          genreVehic: 'm', place: 'places' },
    { sujet: 'personnes', sujet_sg: 'personne', genreSujet: 'f', vehic_pl: 'minibus',      vehic_sg: 'minibus',      genreVehic: 'm', place: 'places' },
    { sujet: 'gâteaux',   sujet_sg: 'gâteau',   genreSujet: 'm', vehic_pl: 'plateaux',     vehic_sg: 'plateau',      genreVehic: 'm', place: 'places' },
];

/** Contextes invites_recalcul (toujours gourmandises pour invités). */
const CTX_INVITES = [
    { objet: 'fondants',  sing: 'fondant',  genreObj: 'm' },
    { objet: 'chocolats', sing: 'chocolat', genreObj: 'm' },
    { objet: 'biscuits',  sing: 'biscuit',  genreObj: 'm' },
    { objet: 'bonbons',   sing: 'bonbon',   genreObj: 'm' },
    { objet: 'gâteaux',   sing: 'gâteau',   genreObj: 'm' },
    { objet: 'muffins',   sing: 'muffin',   genreObj: 'm' },
];

// ═══════════════════════════════════════════════════════════
// HELPERS GRAMMAIRE FRANÇAISE
// ═══════════════════════════════════════════════════════════

/** Élision "de" → "d'" devant voyelle/h muet. Retourne "de " ou "d'" (apostrophe incluse). */
const dePrep = (word) => {
    const first = word.normalize('NFD').replace(/[\u0300-\u036f]/g, '')[0].toLowerCase();
    return /[aeiouh]/.test(first) ? "d'" : 'de ';
};

/** Accord "aucun / aucune" selon le genre. */
const aucunAccord = (genre) => (genre === 'f' ? 'aucune' : 'aucun');

/** Article indéfini "un / une" selon le genre. */
const unAccord = (genre) => (genre === 'f' ? 'une' : 'un');

/** Accord "complet / complète" selon le genre. */
const completAccord = (genre) => (genre === 'f' ? 'complète' : 'complet');

// ═══════════════════════════════════════════════════════════
// EXERCICE 1 — partage_objets (QCM + Saisie)
// ═══════════════════════════════════════════════════════════

// Tirages DYNAMIQUES partage/groupement (revue 2026-08-26, choix prof) :
// 70 % de dividendes < 1000, 30 % à 4 chiffres. Remplacent les pools figés
// pour les formats partage_groupement (le V/F garde son pool pivot).
const pickPartageDyn = () => {
    const b = randInt(2, 9);
    const grand = Math.random() >= 0.7;
    const a = grand ? randInt(1000, 9999) : randInt(Math.max(20, 2 * b), 999);
    const { q, r } = divEucl(a, b);
    return [a, b, q, r];
};
const pickGroupementDyn = () => {
    const b = randInt(5, 50);
    const grand = Math.random() >= 0.7;
    const a = grand ? randInt(1000, 9999) : randInt(Math.max(50, 2 * b), 999);
    const { q, r } = divEucl(a, b);
    return [a, b, q, r];
};

// Espace fine insécable des milliers (revue 2026-08-31) : AFFICHAGE des
// dividendes dans les énoncés uniquement — jamais dans une réponse à taper
// ni dans les choix (les solveurs et le correcteur comparent des nombres nus).
const espMilliers619 = (n) => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

const buildEnoncePartage = (prenom, a, ctx, b) => {
    // Format canonique : conserve "partager équitablement ... entre ..." pour
    // l'extraction, mais précise explicitement qu'il s'agit d'un nombre entier.
    const deObj = dePrep(ctx.objet);
    const intros = [
        `${prenom.nom} veut partager équitablement ${espMilliers619(a)} ${ctx.objet} entre ${b} ${ctx.rec_pl}, en donnant à chacun le même nombre entier ${deObj}${ctx.objet}.`,
        `${prenom.nom} doit partager équitablement ${espMilliers619(a)} ${ctx.objet} entre ${b} ${ctx.rec_pl} : chacun doit recevoir le même nombre entier ${deObj}${ctx.objet}.`,
    ];
    const intro = randomChoice(intros);
    const question = `Combien chaque ${ctx.rec_sg} reçoit-il ${deObj}${ctx.objet}, et combien en reste-t-il ?`;
    return `${RAPPEL_BROUILLON}<p>${intro} ${question}</p>`;
};

const formatPartageCouple = (q, r, ctx) => {
    const objQ = q === 1 ? ctx.sing : ctx.objet;
    if (r === 0) {
        return `Chacun reçoit ${q} ${objQ} et il n'en reste ${aucunAccord(ctx.genreObj)}.`;
    }
    return `Chacun reçoit ${q} ${objQ} et il en reste ${r}.`;
};

const genPartageQcm = () => {
    const [a, b, q, r] = pickPartageDyn();
    const ctx = randomChoice(CTX_PARTAGE);
    const prenom = pickPrenom();
    const bonne = formatPartageCouple(q, r, ctx);

    // Distracteurs (fiche §6 A) :
    //  D1 = (q, 0) oubli du reste si r>0
    //  D2 = (q-1, r+b) reste trop grand
    //  D3 = (q+1, r-b) quotient surévalué si valide
    //  D4 = (r, q) couple inversé
    //  D5 = multiplicatif a*b
    // Revue 2026-08-25 (choix prof) : le distracteur multiplicatif a × b est
    // retiré — « Chacun reçoit 78 billes » sur un stock de 26 s'éliminait par
    // simple vraisemblance (A3), sans aucune division.
    const candidats = [];
    if (r > 0) candidats.push(formatPartageCouple(q, 0, ctx));
    candidats.push(formatPartageCouple(q - 1, r + b, ctx));
    if (r - b >= 0) candidats.push(formatPartageCouple(q + 1, r - b, ctx));
    candidats.push(formatPartageCouple(r, q, ctx));

    const distracteurs = cleanDistracteurs(bonne, candidats, 4, (i) => {
        if (i === 0) return formatPartageCouple(q + 1, 0, ctx);
        if (i === 1) return formatPartageCouple(q, r + 1, ctx);
        if (i === 2) return formatPartageCouple(Math.max(0, q - 1), Math.max(0, r - 1), ctx);
        return formatPartageCouple(q + 2, 0, ctx);
    });

    const choix = [bonne, ...distracteurs];
    const payload = makeQcm(choix, bonne, buildEnoncePartage(prenom, a, ctx, b), opts(L_PARTAGE_QCM));
    // Calculatrice RETIRÉE (décision prof 2026-08-31) : la division
    // euclidienne demandée reste dans le programme de 6e.
    payload._meta.generatorKey = 'partage_objets';
    return payload;
};

const genPartageSaisie = () => {
    const [a, b, q, r] = pickPartageDyn();
    const ctx = randomChoice(CTX_PARTAGE);
    const prenom = pickPrenom();
    const objQ = q === 1 ? ctx.sing : ctx.objet;
    const phrase = `chaque ${ctx.rec_sg} reçoit {{${q}}} ${objQ} et il en reste {{${r}}}`;
    const payload = makeTexteTrou(
        phrase,
        buildEnoncePartage(prenom, a, ctx, b),
        { ...opts(L_PARTAGE_SAI), mode: 'saisie', strict: false },
    );
    // Calculatrice RETIRÉE (décision prof 2026-08-31) : la division
    // euclidienne demandée reste dans le programme de 6e.
    payload._meta.generatorKey = 'partage_objets';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 2 — groupement_paquets (QCM + Saisie)
// ═══════════════════════════════════════════════════════════

const buildEnonceGroupement = (prenom, a, ctx, b) => {
    // Format canonique : "{Prenom} dispose de {a} {objet}. Il souhaite en faire des {grp_pl} de {b} {objet} chacun."
    const il = prenom.fem ? 'Elle' : 'Il';
    const deObj = dePrep(ctx.objet);
    const intros = [
        `${prenom.nom} dispose de ${espMilliers619(a)} ${ctx.objet}. ${il} souhaite en faire des ${ctx.grp_pl} de ${b} ${ctx.objet} chacun.`,
        `${prenom.nom} dispose de ${espMilliers619(a)} ${ctx.objet} et veut en faire des ${ctx.grp_pl} de ${b} ${ctx.objet} chacun.`,
    ];
    const intro = randomChoice(intros);
    const question = `Combien de ${ctx.grp_pl} peut-${prenom.fem ? 'elle' : 'il'} former, et combien ${deObj}${ctx.objet} lui restera-t-il ?`;
    return `${RAPPEL_BROUILLON}<p>${intro} ${question}</p>`;
};

const formatGroupementCouple = (q, r, ctx) => {
    const grpQ = q === 1 ? ctx.grp_sg : ctx.grp_pl;
    if (r === 0) {
        return `${q} ${grpQ} et il ne reste ${aucunAccord(ctx.genreObj)} ${ctx.sing}.`;
    }
    const objR = r === 1 ? ctx.sing : ctx.objet;
    return `${q} ${grpQ} et il reste ${r} ${objR}.`;
};

const genGroupementQcm = () => {
    const [a, b, q, r] = pickGroupementDyn();
    const ctx = randomChoice(CTX_GROUPEMENT);
    const prenom = pickPrenom();
    const bonne = formatGroupementCouple(q, r, ctx);

    // Revue 2026-08-25 (choix prof) : le piège décimal « environ 16,39
    // bouquets » est retiré — seul choix avec « environ » et une virgule, il
    // se repérait à sa forme dans 100 % des tirages (A3), comme au gen 618.
    const candidats = [];
    if (r - b >= 0) candidats.push(formatGroupementCouple(q + 1, r - b, ctx));
    if (r > 0) candidats.push(formatGroupementCouple(q, 0, ctx));
    candidats.push(formatGroupementCouple(r, q, ctx));
    candidats.push(formatGroupementCouple(q - 1, r + b, ctx));

    const distracteurs = cleanDistracteurs(bonne, candidats, 4, (i) => {
        if (i === 0) return formatGroupementCouple(q + 1, 0, ctx);
        if (i === 1) return formatGroupementCouple(Math.max(0, q - 1), 0, ctx);
        if (i === 2) return formatGroupementCouple(q + 2, Math.max(0, r - 1), ctx);
        return formatGroupementCouple(q, Math.max(0, r - 1), ctx);
    });

    const choix = [bonne, ...distracteurs];
    const payload = makeQcm(choix, bonne, buildEnonceGroupement(prenom, a, ctx, b), opts(L_GROUPEMENT_QCM));
    // Calculatrice RETIRÉE (décision prof 2026-08-31) : la division
    // euclidienne demandée reste dans le programme de 6e.
    payload._meta.generatorKey = 'groupement_paquets';
    return payload;
};

const genGroupementSaisie = () => {
    const [a, b, q, r] = pickGroupementDyn();
    const ctx = randomChoice(CTX_GROUPEMENT);
    const prenom = pickPrenom();
    const grpQ = q === 1 ? ctx.grp_sg : ctx.grp_pl;
    const objR = r === 1 ? ctx.sing : ctx.objet;
    const phrase = `{{${q}}} ${grpQ} et il reste {{${r}}} ${objR}`;
    const payload = makeTexteTrou(
        phrase,
        buildEnonceGroupement(prenom, a, ctx, b),
        { ...opts(L_GROUPEMENT_SAI), mode: 'saisie', strict: false },
    );
    // Calculatrice RETIRÉE (décision prof 2026-08-31) : la division
    // euclidienne demandée reste dans le programme de 6e.
    payload._meta.generatorKey = 'groupement_paquets';
    return payload;
};

// ─── Wrappers fusion partage + groupement ───
// Les deux contextes testent strictement le même skill mathématique (a ÷ b → lire q et r),
// seul le vocabulaire diffère ("partager entre" vs "grouper en paquets de"). On les pioche
// aléatoirement à chaque génération pour offrir de la variété sans dupliquer la catégorie.
const genPartageOuGroupementQcm    = () => (Math.random() < 0.5 ? genPartageQcm()    : genGroupementQcm());
const genPartageOuGroupementSaisie = () => (Math.random() < 0.5 ? genPartageSaisie() : genGroupementSaisie());

// ═══════════════════════════════════════════════════════════
// EXERCICE 3 — complement_groupe_plus (QCM + Saisie)
// ═══════════════════════════════════════════════════════════

const buildEnonceComplement = (prenom, a, ctx, b) => {
    // Format canonique : "{Prenom} dispose de {a} {objet}. Il les répartit en {grp_pl} de {b} {objet}."
    const il = prenom.fem ? 'Elle' : 'Il';
    const deObj = dePrep(ctx.objet);
    const intros = [
        `${prenom.nom} dispose de ${espMilliers619(a)} ${ctx.objet}. ${il} les répartit en ${ctx.grp_pl} de ${b} ${ctx.objet}.`,
        `${prenom.nom} a ${espMilliers619(a)} ${ctx.objet} et les répartit en ${ctx.grp_pl} de ${b} ${ctx.objet}.`,
    ];
    const intro = randomChoice(intros);
    const unG = unAccord(ctx.genreGrp);
    const compG = completAccord(ctx.genreGrp);
    const question = `Combien ${deObj}${ctx.objet} lui manquerait-il pour faire ${unG} ${ctx.grp_sg} ${compG} de plus ?`;
    return `${RAPPEL_BROUILLON}<p>${intro} ${question}</p>`;
};

const genComplementQcm = () => {
    const [a, b, q, r] = randomChoice(POOL_C_V);   // r > 0 garanti
    const ctx = randomChoice(CTX_GROUPEMENT);
    const prenom = pickPrenom();
    const complement = b - r;
    // Helper local pour accord singulier/pluriel
    const obj = (n) => (n === 1 ? ctx.sing : ctx.objet);
    const bonne = `${complement} ${obj(complement)}`;

    // Distracteurs (fiche §6 C) :
    //  r seul (confusion), b (taille du groupe), q (nombre de groupes), b+r, b-r-1
    const candidats = [
        `${r} ${obj(r)}`,
        `${b} ${obj(b)}`,
        `${q} ${obj(q)}`,
        `${b + r} ${obj(b + r)}`,
    ];
    if (b - r - 1 > 0 && b - r - 1 !== r) candidats.push(`${b - r - 1} ${obj(b - r - 1)}`);

    const distracteurs = cleanDistracteurs(bonne, candidats, 4, (i) => {
        if (i === 0) return `${b + 1} ${obj(b + 1)}`;
        if (i === 1) return `${Math.max(0, b - r + 1)} ${obj(Math.max(0, b - r + 1))}`;
        if (i === 2) return `${r + 1} ${obj(r + 1)}`;
        return `${q + r} ${obj(q + r)}`;
    });

    const choix = [bonne, ...distracteurs];
    const payload = makeQcm(choix, bonne, buildEnonceComplement(prenom, a, ctx, b), opts(L_COMPLEMENT_QCM));
    // Calculatrice RETIRÉE (décision prof 2026-08-31) : la division
    // euclidienne et le complément restent dans le programme de 6e.
    payload._meta.generatorKey = 'complement_groupe_plus';
    return payload;
};

const genComplementSaisie = () => {
    // Filtrage : complement = b - r doit être >= 2 (évite "1 cartes" dans le suffixe plural)
    const pool = POOL_C_V.filter(([, b, , r]) => b - r >= 2);
    const [a, b, q, r] = randomChoice(pool);
    const ctx = randomChoice(CTX_GROUPEMENT);
    const prenom = pickPrenom();
    const complement = b - r;
    const payload = makeSaisie(
        String(complement),
        buildEnonceComplement(prenom, a, ctx, b),
        { ...opts(L_COMPLEMENT_SAI), suffixe: ' ' + ctx.objet },
    );
    // Calculatrice RETIRÉE (décision prof 2026-08-31) : la division
    // euclidienne et le complément restent dans le programme de 6e.
    payload._meta.generatorKey = 'complement_groupe_plus';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 4 — transport_min (QCM + Saisie)
// ═══════════════════════════════════════════════════════════

// Tirage DYNAMIQUE transport_min (revue 2026-08-25, choix prof) : le pool
// figé plafonnait à 120 énoncés (40 répétitions immédiates / 5000).
// b ∈ [4, 60], a ∈ [b, 600] ; 1 tirage sur 4 force r = 0 pour préserver les
// cas « ça tombe juste » (réponse = q, sans le +1).
const pickTransportMin = () => {
    // 70/30 (revue 2026-08-26, choix prof) : 30 % de dividendes à 4 chiffres.
    // Vraisemblance : en tirage « grand », b >= 20 et q <= 60 (jamais des
    // centaines de véhicules).
    const grand = Math.random() >= 0.7;
    const b = grand ? randInt(20, 60) : randInt(4, 60);
    if (Math.random() < 0.25) {
        const qMin = grand ? Math.ceil(1000 / b) : 1;
        const qMax = grand ? 60 : Math.floor(600 / b);
        if (qMax >= qMin) {
            const q = randInt(qMin, qMax);
            return [b * q, b, q, 0];
        }
    }
    if (grand) {
        const q = randInt(Math.ceil(1000 / b), 60);
        const r = randInt(1, b - 1);
        return [b * q + r, b, q, r];
    }
    const a = randInt(b, 600);
    const { q, r } = divEucl(a, b);
    return [a, b, q, r];
};

const buildEnonceTransportMin = (a, ctx, b) => {
    // Format canonique : "{a} {sujet} doivent être transporté(e)s dans des {vehic_pl} de {b} places."
    const participe = ctx.genreSujet === 'f' ? 'transportées' : 'transportés';
    // "chacun / chacune" accorde avec vehic_pl, mais ici sous-entendu "par véhicule" : on met chacun/une au genre du contenant…
    // Plus simple : abandon de "chacun", suffixe moins ambigu.
    const doivent = `${espMilliers619(a)} ${ctx.sujet} doivent être ${participe} dans des ${ctx.vehic_pl} de ${b} ${ctx.place}`;
    const intro = `${doivent}.`;
    const tous = ctx.genreSujet === 'f' ? 'toutes les' : 'tous les';
    const deVeh = dePrep(ctx.vehic_pl);
    const question = `Combien ${deVeh}${ctx.vehic_pl} faut-il au minimum pour transporter ${tous} ${ctx.sujet} ?`;
    return `${RAPPEL_BROUILLON}<p>${intro} ${question}</p>`;
};

// Anti-répétition immédiate (revue 2026-08-25, recette standard).
let derniereSignatureTransportMinQcm619 = null;

const genTransportMinQcm = () => {
    let payload = construireTransportMinQcm();
    for (let t = 0; t < 8 && payload.texte === derniereSignatureTransportMinQcm619; t++) {
        payload = construireTransportMinQcm();
    }
    derniereSignatureTransportMinQcm619 = payload.texte;
    return payload;
};

const construireTransportMinQcm = () => {
    const [a, b, q, r] = pickTransportMin();
    const ctx = randomChoice(CTX_TRANSPORT);
    const nb = ceilDiv(a, b);  // réponse exacte
    const veh = (n) => (n === 1 ? ctx.vehic_sg : ctx.vehic_pl);
    const bonne = `${nb} ${veh(nb)}`;

    // Distracteurs (fiche §6 D) :
    //  q seul (oubli du +1) si r>0, ou q+1 si r=0
    //  q+2 (sur-ajout)
    //  q*2, r seul
    const candidats = [
        r > 0 ? `${q} ${veh(q)}` : `${q + 1} ${veh(q + 1)}`,
        `${nb + 1} ${veh(nb + 1)}`,
        `${q * 2} ${veh(q * 2)}`,
        `${Math.max(1, r)} ${veh(Math.max(1, r))}`,
    ];
    if (r > 0 && q - 1 >= 0) candidats.push(`${q - 1} ${veh(q - 1)}`);

    const distracteurs = cleanDistracteurs(bonne, candidats, 4, (i) => {
        if (i === 0) return `${nb + 2} ${veh(nb + 2)}`;
        if (i === 1) return `${Math.max(1, nb - 1)} ${veh(Math.max(1, nb - 1))}`;
        if (i === 2) return `${nb + 3} ${veh(nb + 3)}`;
        return `${nb * 2} ${veh(nb * 2)}`;
    });

    const choix = [bonne, ...distracteurs];
    const payload = makeQcm(choix, bonne, buildEnonceTransportMin(a, ctx, b), opts(L_TRANSPORT_MIN_QCM));
    // Calculatrice RETIRÉE (décision prof 2026-08-31) : la division
    // euclidienne demandée reste dans le programme de 6e.
    payload._meta.generatorKey = 'transport_min';
    return payload;
};

// Anti-répétition immédiate (revue 2026-08-25, recette standard).
let derniereSignatureTransportMinSai619 = null;

const genTransportMinSaisie = () => {
    let payload = construireTransportMinSaisie();
    for (let t = 0; t < 8 && payload.texte === derniereSignatureTransportMinSai619; t++) {
        payload = construireTransportMinSaisie();
    }
    derniereSignatureTransportMinSai619 = payload.texte;
    return payload;
};

const construireTransportMinSaisie = () => {
    const [a, b, q, r] = pickTransportMin();
    const ctx = randomChoice(CTX_TRANSPORT);
    const nb = ceilDiv(a, b);
    const payload = makeSaisie(
        String(nb),
        buildEnonceTransportMin(a, ctx, b),
        { ...opts(L_TRANSPORT_MIN_SAI), suffixe: ' ' + ctx.vehic_pl },
    );
    // Calculatrice RETIRÉE (décision prof 2026-08-31) : la division
    // euclidienne demandée reste dans le programme de 6e.
    payload._meta.generatorKey = 'transport_min';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 5 — transport_dernier (QCM + Saisie)
// ═══════════════════════════════════════════════════════════

// Tirage DYNAMIQUE transport_dernier (revue 2026-08-25, choix prof) : le pool
// figé plafonnait à 144 énoncés (33 répétitions immédiates / 5000).
// b ∈ [4, 60], q ∈ [1, 15], r ∈ [2, b−1] garanti (le dernier contenant est
// toujours partiellement rempli, jamais « 1 passagers »).
const pickTransportDernier = () => {
    // 70/30 (revue 2026-08-26, choix prof) : 30 % de dividendes à 4 chiffres.
    // Vraisemblance : en tirage « grand », b >= 25 et q <= 60.
    const grand = Math.random() >= 0.7;
    for (let t = 0; t < 20; t++) {
        const b = grand ? randInt(25, 60) : randInt(4, 60);
        const q = grand ? randInt(Math.ceil(1000 / b), 60) : randInt(1, 15);
        const r = randInt(2, b - 1);
        const a = b * q + r;
        if (grand ? (a >= 1000 && a <= 9999) : a <= 600) return [a, b, q, r];
    }
    return [95, 20, 4, 15]; // repli sûr (ancien pool)
};

const buildEnonceTransportDernier = (a, ctx, b) => {
    // Format canonique : "Pour transporter {a} {sujet}, on utilise des {vehic_pl} de {b} places, remplis au maximum."
    const remplis = ctx.genreVehic === 'f' ? 'remplies' : 'remplis';
    const intros = [
        `Pour transporter ${espMilliers619(a)} ${ctx.sujet}, on utilise des ${ctx.vehic_pl} de ${b} ${ctx.place}, ${remplis} au maximum.`,
        `Pour transporter ${espMilliers619(a)} ${ctx.sujet}, on remplit des ${ctx.vehic_pl} de ${b} ${ctx.place} au maximum.`,
    ];
    const intro = randomChoice(intros);
    const deSuj = dePrep(ctx.sujet);
    const dernier = ctx.genreVehic === 'f' ? 'la dernière' : 'le dernier';
    const question = `Combien ${deSuj}${ctx.sujet} prendront place dans ${dernier} ${ctx.vehic_sg} ?`;
    return `${RAPPEL_BROUILLON}<p>${intro} ${question}</p>`;
};

// Anti-répétition immédiate (revue 2026-08-25, recette standard).
let derniereSignatureTransportDerQcm619 = null;

const genTransportDernierQcm = () => {
    let payload = construireTransportDernierQcm();
    for (let t = 0; t < 8 && payload.texte === derniereSignatureTransportDerQcm619; t++) {
        payload = construireTransportDernierQcm();
    }
    derniereSignatureTransportDerQcm619 = payload.texte;
    return payload;
};

const construireTransportDernierQcm = () => {
    const [a, b, q, r] = pickTransportDernier();   // r >= 2 garanti
    const ctx = randomChoice(CTX_TRANSPORT);
    const suj = (n) => (n === 1 ? ctx.sujet_sg : ctx.sujet);
    const bonne = `${r} ${suj(r)}`;

    // Distracteurs (fiche §6 E) :
    //  b (dernier contenant plein), q (confusion), b-r (complément), b*q, r+1
    const candidats = [
        `${b} ${suj(b)}`,
        `${q} ${suj(q)}`,
        `${b - r} ${suj(b - r)}`,
        `${b * q} ${suj(b * q)}`,
    ];
    if (r + 1 !== b && r + 1 !== b - r) candidats.push(`${r + 1} ${suj(r + 1)}`);

    const distracteurs = cleanDistracteurs(bonne, candidats, 4, (i) => {
        if (i === 0) return `${b + 1} ${suj(b + 1)}`;
        if (i === 1) return `${Math.max(0, r - 1)} ${suj(Math.max(0, r - 1))}`;
        if (i === 2) return `${q + 1} ${suj(q + 1)}`;
        return `${a} ${suj(a)}`;
    });

    const choix = [bonne, ...distracteurs];
    const payload = makeQcm(choix, bonne, buildEnonceTransportDernier(a, ctx, b), opts(L_TRANSPORT_DER_QCM));
    // Calculatrice RETIRÉE (décision prof 2026-08-31) : la division
    // euclidienne demandée reste dans le programme de 6e.
    payload._meta.generatorKey = 'transport_dernier';
    return payload;
};

// Anti-répétition immédiate (revue 2026-08-25, recette standard).
let derniereSignatureTransportDerSai619 = null;

const genTransportDernierSaisie = () => {
    let payload = construireTransportDernierSaisie();
    for (let t = 0; t < 8 && payload.texte === derniereSignatureTransportDerSai619; t++) {
        payload = construireTransportDernierSaisie();
    }
    derniereSignatureTransportDerSai619 = payload.texte;
    return payload;
};

const construireTransportDernierSaisie = () => {
    const [a, b, q, r] = pickTransportDernier();
    const ctx = randomChoice(CTX_TRANSPORT);
    const payload = makeSaisie(
        String(r),
        buildEnonceTransportDernier(a, ctx, b),
        { ...opts(L_TRANSPORT_DER_SAI), suffixe: ' ' + ctx.sujet },
    );
    // Calculatrice RETIRÉE (décision prof 2026-08-31) : la division
    // euclidienne demandée reste dans le programme de 6e.
    payload._meta.generatorKey = 'transport_dernier';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 6 — invites_recalcul (Saisie uniquement)
// ═══════════════════════════════════════════════════════════

const buildEnonceInvites = (prenom, a, ctx, b0, k) => {
    // Format canonique : "{Prenom} a préparé {a} {objet} pour ses {b0} invités. Mais {k} invités supplémentaires arrivent."
    const il = prenom.fem ? 'Elle' : 'Il';
    const intro = `${prenom.nom} a préparé ${espMilliers619(a)} ${ctx.objet} pour ses ${b0} invités. Mais ${k} invités supplémentaires arrivent. ${il} décide de répartir équitablement les ${ctx.objet} entre tous les invités.`;
    const question = `Combien chaque invité recevra-t-il de ${ctx.objet} maintenant, et combien en restera-t-il ?`;
    return `${RAPPEL_BROUILLON}<p>${intro} ${question}</p>`;
};

// Anti-répétition immédiate (revue 2026-08-25, recette standard).
let derniereSignatureInvites619 = null;

const genInvitesSaisie = () => {
    let payload = construireInvitesSaisie();
    for (let t = 0; t < 8 && payload.texte === derniereSignatureInvites619; t++) {
        payload = construireInvitesSaisie();
    }
    derniereSignatureInvites619 = payload.texte;
    return payload;
};

const construireInvitesSaisie = () => {
    const [a, b0, k] = randomChoice(POOL_F_V);
    const b = b0 + k;
    const { q, r } = divEucl(a, b);
    const ctx = randomChoice(CTX_INVITES);
    const prenom = pickPrenom();
    const phrase = `chaque invité reçoit {{${q}}} ${ctx.objet} et il en reste {{${r}}}`;
    const payload = makeTexteTrou(
        phrase,
        buildEnonceInvites(prenom, a, ctx, b0, k),
        { ...opts(L_INVITES), mode: 'saisie', strict: false },
    );
    // Calculatrice RETIRÉE (décision prof 2026-08-31) : la division
    // euclidienne demandée reste dans le programme de 6e.
    payload._meta.generatorKey = 'invites_recalcul';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 7 — choisir_calculs_eucl (QCM multiple uniquement)
// ═══════════════════════════════════════════════════════════

/**
 * Contextes pour « choisir les écritures » — la situation pose une question de
 * groupement ; unite/objet accordent les écritures proposées.
 * (Enrichissement 2026-07-04 : nombres GÉNÉRÉS par construction au lieu de
 * 6 pivots figés — l'élève JUGE les écritures, il ne calcule pas.)
 */
const CONTEXTES_CHOISIR = [
    { situation: (a, b) => `Un fleuriste veut savoir combien de bouquets de ${b} roses il peut composer avec ${espMilliers619(a)} roses.`, unite: 'bouquets', objet: 'roses' },
    { situation: (a, b) => `Une fermière veut savoir combien de boîtes de ${b} œufs elle peut remplir avec ${espMilliers619(a)} œufs.`, unite: 'boîtes', objet: 'œufs' },
    { situation: (a, b) => `Un professeur veut savoir combien d'équipes de ${b} élèves il peut former avec ${espMilliers619(a)} élèves.`, unite: 'équipes', objet: 'élèves' },
    { situation: (a, b) => `Une bibliothécaire veut savoir combien de cartons de ${b} livres elle peut remplir avec ${espMilliers619(a)} livres.`, unite: 'cartons', objet: 'livres' },
    { situation: (a, b) => `Malik veut savoir combien de sachets de ${b} billes il peut préparer avec ${espMilliers619(a)} billes.`, unite: 'sachets', objet: 'billes' },
    { situation: (a, b) => `Le gardien du gymnase veut savoir combien de rangées de ${b} chaises il peut installer avec ${espMilliers619(a)} chaises.`, unite: 'rangées', objet: 'chaises' },
    { situation: (a, b) => `Une pâtissière veut savoir combien de plateaux de ${b} gâteaux elle peut composer avec ${espMilliers619(a)} gâteaux.`, unite: 'plateaux', objet: 'gâteaux' },
    { situation: (a, b) => `Nora veut savoir combien de pages de ${b} photos elle peut remplir avec ${espMilliers619(a)} photos.`, unite: 'pages', objet: 'photos' },
];

const genChoisirCalculsEucl = () => {
    // Nombres générés par construction : a = b × q + r ⇒ l'égalité euclidienne
    // est vraie par fabrication et le reste est valide (1 ≤ r < b)
    // 70/30 (revue 2026-08-26, choix prof) : 30 % de dividendes à 4 chiffres
    // (b >= 12 et q <= 99 en tirage « grand », pour rester vraisemblable).
    const grand = Math.random() >= 0.7;
    const b = grand ? randInt(12, 25) : randInt(6, 25);
    const q = grand ? randInt(Math.ceil(1000 / b), 99) : randInt(8, 30);
    const r = randInt(1, b - 1);
    const a = b * q + r;
    const ctx = randomChoice(CONTEXTES_CHOISIR);

    // Écritures CORRECTES (toujours présentes)
    const optA = `${a} = ${b} × ${q} + ${r}, donc on obtient ${q} ${ctx.unite} et il reste ${r} ${ctx.objet}.`;
    const optB = `${a} divisé par ${b} donne ${q} avec un reste de ${r}, donc ${q} ${ctx.unite}.`;

    // Pièges — rotation de 2 parmi 4 familles d'erreurs résiduelles
    const piegeDecimal = `${a} ÷ ${b} ≈ ${toDecApprox(a, b)}, donc environ ${toDecApprox(a, b)} ${ctx.unite}.`;
    const piegeInverse = `${b} ÷ ${a} = ${(b / a).toFixed(3).replace('.', ',')}, donc 0 ${ctx.unite}.`;
    const piegeQuotientPlusUn = `${a} = ${b} × ${q} + ${r}, donc on obtient ${q + 1} ${ctx.unite}.`;
    // Égalité VRAIE mais division mal terminée : le reste (r + b) dépasse le
    // diviseur — seul un élève qui connaît la règle r < b peut la rejeter
    const piegeResteTropGrand = `${a} = ${b} × ${q - 1} + ${r + b}, donc on obtient ${q - 1} ${ctx.unite} et il reste ${r + b} ${ctx.objet}.`;
    // Quota VARIABLE de vraies (revue 2026-08-25, choix prof) : 1 ou 2 — le
    // « toujours 2 vraies » constant était comptable, comme au gen 617.
    const nbVraies = randInt(1, 2);
    const correct = shuffle([optA, optB]).slice(0, nbVraies);
    const pieges = randomChoiceMultiple(
        [piegeDecimal, piegeInverse, piegeQuotientPlusUn, piegeResteTropGrand],
        4 - nbVraies,
    );

    const choix = [...correct, ...pieges];
    const texte = `<p>${ctx.situation(a, b)} Parmi ces calculs, lesquels permettent de répondre correctement à la question ? <em>(plusieurs réponses possibles)</em></p>`;

    const payload = makeQcm(choix, correct, texte, opts(L_CHOISIR), true);
    payload._meta.generatorKey = 'choisir_calculs_eucl';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 8 — relier_enonces_eucl (Relier uniquement)
// ═══════════════════════════════════════════════════════════

/**
 * Jeux de 4 énoncés partageant LES MÊMES 2 nombres mais nécessitant des
 * opérations différentes (+, −, ×, ÷). L'élève doit identifier le verbe et la
 * structure sémantique de chaque phrase pour reconnaître la bonne opération —
 * impossible de pattern-matcher les nombres puisqu'ils sont identiques partout.
 *
 * Chaque jeu = 4 sources + 4 cibles avec les opérations a+b, a−b, a×b, a÷b.
 * Pour la division, certaines sources sont "renversées" (le diviseur apparaît
 * AVANT le dividende dans la phrase) pour tester explicitement la lecture.
 */
const JEUX_RELIER = [
    // Set 1 : (84, 7) — division simple
    [
        { source: 'Léa a 84 billes. Tom lui en offre 7 de plus.', target: '84 + 7' },
        { source: 'Léa a 84 billes. Elle en donne 7 à son frère.', target: '84 − 7' },
        { source: 'Léa achète 7 paquets contenant chacun 84 billes.', target: '84 × 7' },
        { source: 'Léa partage 84 billes équitablement entre 7 amis.', target: '84 ÷ 7' },
    ],
    // Set 2 : (96, 6) — diviseur en début de phrase
    [
        { source: 'Hugo a 96 cartes. Maya lui en donne 6 de plus.', target: '96 + 6' },
        { source: 'Hugo a 96 cartes. Il en perd 6 dans la cour.', target: '96 − 6' },
        { source: 'Hugo dispose de 6 boîtes de 96 cartes chacune.', target: '96 × 6' },
        { source: '6 amis se partagent équitablement les 96 cartes de Hugo.', target: '96 ÷ 6' },
    ],
    // Set 3 : (72, 8)
    [
        { source: 'Zoé a 72 perles. Sa sœur lui en donne 8 supplémentaires.', target: '72 + 8' },
        { source: 'Zoé a 72 perles. Elle en utilise 8 pour son bracelet.', target: '72 − 8' },
        { source: 'Zoé fabrique 8 colliers avec 72 perles chacun.', target: '72 × 8' },
        { source: 'Zoé répartit ses 72 perles dans 8 boîtes identiques.', target: '72 ÷ 8' },
    ],
    // Set 4 : (108, 9)
    [
        { source: 'Tom a 108 cartes Pokémon. Léa lui en offre 9 de plus.', target: '108 + 9' },
        { source: 'Tom a 108 cartes. Il en échange 9 avec un copain.', target: '108 − 9' },
        { source: 'Tom range ses cartes dans 9 albums de 108 cartes chacun.', target: '108 × 9' },
        { source: 'Tom partage 108 cartes équitablement entre ses 9 amis.', target: '108 ÷ 9' },
    ],
    // Set 5 : (60, 5) — diviseur en début
    [
        { source: 'Maya a 60 stickers. Sa cousine lui en donne 5 de plus.', target: '60 + 5' },
        { source: 'Maya a 60 stickers. Elle en colle 5 sur son cahier.', target: '60 − 5' },
        { source: 'Maya colle 5 stickers sur chacune de ses 60 pages.', target: '60 × 5' },
        { source: '5 amis se partagent équitablement les 60 stickers de Maya.', target: '60 ÷ 5' },
    ],
    // Set 6 : (48, 4)
    [
        { source: 'Noé a déjà 48 billes. Son frère lui en offre 4 de plus.', target: '48 + 4' },
        { source: 'Noé a 48 billes. Il en perd 4 pendant la récréation.', target: '48 − 4' },
        { source: 'Noé achète 4 sachets de 48 billes chacun.', target: '48 × 4' },
        { source: 'Noé partage 48 billes entre 4 enfants équitablement.', target: '48 ÷ 4' },
    ],
];

/** Objets discrets pour les énoncés de division euclidienne. */
const OBJETS_RELIER_EUCL = [
    { plu: 'billes', sing: 'bille' },
    { plu: 'cartes', sing: 'carte' },
    { plu: 'perles', sing: 'perle' },
    { plu: 'stickers', sing: 'sticker' },
    { plu: 'bonbons', sing: 'bonbon' },
    { plu: 'timbres', sing: 'timbre' },
    { plu: 'coquillages', sing: 'coquillage' },
];

/** Contextes pour +/− variés. */
const RELIER_EUCL_GAIN = ['en offre de plus', 'en donne supplémentaires', 'en offre en cadeau'];
const RELIER_EUCL_PERTE = ['en perd dans la cour', 'en donne à son frère', 'en utilise pour un bricolage'];

/**
 * Génère 4 paires (énoncé, calcul) avec (a, b) tel que a % b === 0
 * (partage équitable cohérent pour objets discrets).
 */
const genRelierEuclData = () => {
    let a, b;
    let attempts = 0;
    do {
        a = randInt(48, 144);
        b = randInt(3, 9);
        attempts++;
    } while (a % b !== 0 && attempts < 50);
    if (a % b !== 0) return null;
    const prenom = pickPrenom();
    const obj = randomChoice(OBJETS_RELIER_EUCL);
    const Elle = prenom.fem ? 'Elle' : 'Il';
    const sibling = prenom.fem ? 'Sa sœur' : 'Son frère';
    const gainCtx = randomChoice(RELIER_EUCL_GAIN);
    const perteCtx = randomChoice(RELIER_EUCL_PERTE);
    return [
        { source: `${prenom.nom} a ${a} ${obj.plu}. ${sibling} lui ${gainCtx === 'en offre de plus' ? 'en offre ' + b + ' de plus' : (gainCtx === 'en donne supplémentaires' ? 'en donne ' + b + ' supplémentaires' : 'en offre ' + b + ' en cadeau')}.`, target: `${a} + ${b}` },
        { source: `${prenom.nom} a ${a} ${obj.plu}. ${Elle} ${perteCtx === 'en perd dans la cour' ? 'en perd ' + b + ' dans la cour' : (perteCtx === 'en donne à son frère' ? 'en donne ' + b + ' à son frère' : 'en utilise ' + b + ' pour un bricolage')}.`, target: `${a} − ${b}` },
        { source: `${prenom.nom} achète ${b} paquets contenant chacun ${a} ${obj.plu}.`, target: `${a} × ${b}` },
        { source: `${prenom.nom} partage ${a} ${obj.plu} équitablement entre ${b} amis.`, target: `${a} ÷ ${b}` },
    ];
};

const genRelierEnoncesEucl = () => {
    // Génération dynamique : ~130 couples × 20 prénoms × 7 objets ≈ 18k combinaisons
    const pairs = genRelierEuclData() || randomChoice(JEUX_RELIER);
    const payload = makeRelier(
        pairs,
        `<p>Associe chaque énoncé à l'opération qui permet de le résoudre.</p>`,
        opts(L_RELIER),
    );
    payload._meta.generatorKey = 'relier_enonces_eucl';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 9 — vrai_faux_interpretation (V/F uniquement)
// ═══════════════════════════════════════════════════════════

/** Contextes « grande échelle » pour les pivots à 4 chiffres du V/F
 *  (revue 2026-08-26) : un acteur institutionnel remplace le prénom —
 *  « Lila partage 5104 crayons entre 6 élèves » n'était pas vraisemblable. */
const CTX_PARTAGE_GRAND = [
    { acteur: 'La mairie', objet: 'livres', sing: 'livre', genreObj: 'm', rec_pl: 'bibliothèques', rec_sg: 'bibliothèque', genreRec: 'f' },
    { acteur: "L'imprimerie", objet: 'cahiers', sing: 'cahier', genreObj: 'm', rec_pl: 'écoles', rec_sg: 'école', genreRec: 'f' },
    { acteur: 'La coopérative', objet: 'bouteilles', sing: 'bouteille', genreObj: 'f', rec_pl: 'magasins', rec_sg: 'magasin', genreRec: 'm' },
    { acteur: 'Le grossiste', objet: 'ballons', sing: 'ballon', genreObj: 'm', rec_pl: 'clubs', rec_sg: 'club', genreRec: 'm' },
    { acteur: "L'entrepôt", objet: 'colis', sing: 'colis', genreObj: 'm', rec_pl: 'camions', rec_sg: 'camion', genreRec: 'm' },
];

const buildEnonceVFInterpretation = (sujetNom, a, ctx, b) => {
    // Format canonique : "{Sujet} partage équitablement {a} {objet} entre {b} {rec_pl}."
    const deObj = dePrep(ctx.objet);
    const chacun = ctx.genreRec === 'f' ? 'chacune' : 'chacun';
    const intro = `${sujetNom} partage équitablement ${espMilliers619(a)} ${ctx.objet} entre ${b} ${ctx.rec_pl}, en donnant le même nombre entier ${deObj}${ctx.objet} à ${chacun}.`;
    return `${RAPPEL_BROUILLON}<p>${intro} Pour chaque affirmation, dis si elle est vraie ou fausse.</p>`;
};

const genVraiFauxInterpretation = () => {
    // Pivot DYNAMIQUE (revue 2026-08-26, choix prof) : 90 % de dividendes
    // < 1000, 10 % à 4 chiffres. Contraintes inchangées : r > 0, q ≥ 1 et
    // 2r ≠ b (sinon V3 et F3 collisionnent).
    const grand = Math.random() >= 0.9;
    let pivot = null;
    for (let t = 0; t < 30 && !pivot; t++) {
        const bT = randInt(2, 9);
        const aT = grand ? randInt(1000, 9999) : randInt(20, 999);
        const { q: qT, r: rT } = divEucl(aT, bT);
        if (rT > 0 && qT >= 1 && 2 * rT !== bT) pivot = [aT, bT, qT, rT];
    }
    if (!pivot) return buildVFFallback();
    const [a, b, q, r] = pivot;
    // Grande échelle → acteur institutionnel ; sinon prénom d'élève.
    const ctx = grand ? randomChoice(CTX_PARTAGE_GRAND) : randomChoice(CTX_PARTAGE);
    const sujetNom = grand ? ctx.acteur : pickPrenom().nom;
    const comp = b - r;
    const unObj = unAccord(ctx.genreObj);
    // Helper local : forme singulier ou pluriel selon le nombre
    const obj = (n) => (n === 1 ? ctx.sing : ctx.objet);

    // Revue 2026-08-25 (choix prof) : UNE seule version par aspect, tirée à
    // pile ou face — l'ancien tableau montrait les deux versions de chaque
    // aspect (« reçoit 21 » ET « reçoit 22 »), paires déductibles + quota
    // 3V/3F constant. Désormais 3 lignes, quota imprévisible (0 à 3 vraies).
    const aspects = [
        {
            vraie: `Chaque ${ctx.rec_sg} reçoit ${q} ${obj(q)}.`,
            fausse: `Chaque ${ctx.rec_sg} reçoit ${q + 1} ${obj(q + 1)}.`,          // quotient surévalué
        },
        {
            vraie: `Il reste ${r} ${obj(r)}.`,
            fausse: `Il reste ${r + b} ${obj(r + b)}.`,                             // reste ≥ diviseur
        },
        {
            vraie: `Il manquerait ${comp} ${obj(comp)} pour pouvoir donner ${unObj} ${ctx.sing} de plus à chaque ${ctx.rec_sg}.`,
            fausse: `Il manquerait ${r} ${obj(r)} pour pouvoir donner ${unObj} ${ctx.sing} de plus à chaque ${ctx.rec_sg}.`, // r au lieu de b − r
        },
    ];
    if (aspects.some((x) => x.vraie === x.fausse)) return buildVFFallback();

    const indexed = aspects.map((x) => (Math.random() < 0.5
        ? { l: x.vraie, br: 0 }
        : { l: x.fausse, br: 1 }));
    const shuf = shuffle(indexed);

    const payload = makeVraiFaux(
        shuf.map((x) => x.l),
        shuf.map((x) => x.br),
        buildEnonceVFInterpretation(sujetNom, a, ctx, b),
        { ...opts(L_VF), colonnes: ['Vraie', 'Fausse'] },
    );
    // Calculatrice COMPLÈTE autorisée (revue 2026-08-25, choix prof) : cf.
    // les autres problèmes du gen.
    payload.contenu = { ...payload.contenu, calculatrice: true };
    payload._meta.generatorKey = 'vrai_faux_interpretation';
    return payload;
};

const buildVFFallback = () => {
    // Pivot fixe : Emma partage 87 billes entre 4 amis. q=21, r=3, b-r=1.
    // 2*3=6 ≠ 4 donc OK.
    const lignes = [
        'Chaque ami reçoit 21 billes.',                                                          // V (q)
        'Il reste 3 billes.',                                                                    // V (r)
        'Il manquerait 1 bille pour pouvoir donner une bille de plus à chaque ami.',             // V (b-r=1)
        'Chaque ami reçoit 22 billes.',                                                          // F (q+1)
        'Il reste 7 billes.',                                                                    // F (r+b=7)
        'Il manquerait 3 billes pour pouvoir donner une bille de plus à chaque ami.',            // F (r)
    ];
    const bonnesReponses = [0, 0, 0, 1, 1, 1];
    const payload = makeVraiFaux(
        lignes, bonnesReponses,
        `${RAPPEL_BROUILLON}<p>Emma partage équitablement 87 billes entre 4 amis, en donnant le même nombre entier de billes à chacun. Pour chaque affirmation, dis si elle est vraie ou fausse.</p>`,
        { ...opts(L_VF), colonnes: ['Vraie', 'Fausse'] },
    );
    payload._meta.generatorKey = 'vrai_faux_interpretation';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// REGISTRE
// ═══════════════════════════════════════════════════════════

const GENERATORS = {
    partage_groupement_qcm_unique:        genPartageOuGroupementQcm,
    partage_groupement_texte:             genPartageOuGroupementSaisie,
    complement_groupe_plus_qcm_unique:    genComplementQcm,
    complement_groupe_plus_texte:         genComplementSaisie,
    transport_min_qcm_unique:             genTransportMinQcm,
    transport_min_texte:                  genTransportMinSaisie,
    transport_dernier_qcm_unique:         genTransportDernierQcm,
    transport_dernier_texte:              genTransportDernierSaisie,
    invites_recalcul_texte:               genInvitesSaisie,
    choisir_calculs_eucl_qcm_multiple:    genChoisirCalculsEucl,
    relier_enonces_eucl_relier:           genRelierEnoncesEucl,
    vrai_faux_interpretation_tableau_vf:  genVraiFauxInterpretation,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', qcm_multiple: 'QCM multiple', texte: 'Saisie',
    relier: 'Relier', tableau_vf: 'Vrai/Faux',
};

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'partage_groupement',         label: L_PARTAGE_GROUPEMENT, formatsList: buildFormats('partage_groupement') },
    { id: 'complement_groupe_plus',     label: L_COMPLEMENT_QCM,     formatsList: buildFormats('complement_groupe_plus') },
    { id: 'transport_min',              label: L_TRANSPORT_MIN_QCM,  formatsList: buildFormats('transport_min') },
    { id: 'transport_dernier',          label: L_TRANSPORT_DER_QCM,  formatsList: buildFormats('transport_dernier') },
    { id: 'invites_recalcul',           label: L_INVITES,            formatsList: buildFormats('invites_recalcul') },
    {
        // Fusion revue 2026-08-31 : le QCM multiple (écritures correctes) et le
        // V/F (interprétations) jugent la MÊME chose — ce qu'on peut dire d'une
        // division euclidienne déjà faite. Deux formats d'une seule tâche.
        id: 'choisir_calculs_eucl',
        label: L_INTERPRETER,
        formatsList: [...buildFormats('choisir_calculs_eucl'), ...buildFormats('vrai_faux_interpretation')],
    },
    // Revue 2026-08-31 (choix prof) : doublon structurel du relier du gen 617
    // (mêmes 4 opérations sur un même couple) — MASQUÉ du catalogue, générateur
    // conservé pour la banque. Sa particularité (diviseur avant le dividende)
    // a été importée dans le 617.
    // { id: 'relier_enonces_eucl',        label: L_RELIER,            formatsList: buildFormats('relier_enonces_eucl') },
];

// Exports utiles au stress test et usages internes
export const _internals = {
    divEucl, isValidEucl, ceilDiv,
    POOL_A_V, POOL_B_V, POOL_C_V, POOL_D_V, POOL_E_V, POOL_F_V,
    CONTEXTES_CHOISIR, JEUX_RELIER,
    GENERATORS,
};

// ─── Point d'entrée ───
export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) =>
            k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    // Toujours forcer la clé complète (les générateurs ne posent que le préfixe
    // catégorie ce qui cassait le "regenerate dans le même format")
    payload._meta.generatorKey = key;
    return payload;
};
